import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Dialog from "@mui/material/Dialog";

import theme from "../../../styles/theme";
import { getFlag } from "../../../utils/helpers/flags";
import msToTime from "../../../utils/helpers/msToTime";
import formatDate from "../../../utils/helpers/formatDate";
import { NoDataMessage } from "../../../components/Common/TableStyles";
import { Dropdown } from "../../../components/Common/Dropdown";
import {
  CloseButton,
  ModalHead,
  ModalHeadText,
  ModalNote,
  ModalTitle,
  modalOverlaySx,
  modalPaperSx,
} from "../styles";
import { tyreImages } from "./tyreImages";
import { SESSION_LABELS } from "./sessions";


const FlagChip = styled.img`
  width: 22px;
  height: auto;
  flex: none;
`;

const ImageArea = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: ${theme.colors.dark};
  overflow: hidden;
`;

const TrackImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const Controls = styled.div`
  padding: 14px 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  border-bottom: 2px solid ${theme.colors.secondaryDark};
`;

const Body = styled.div`
  overflow: auto;
  padding: 14px 18px 18px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const HeadRow = styled.tr`
  height: 24px;

  th {
    text-align: left;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: ${theme.colors.grey};
    border-bottom: 2px solid ${theme.colors.secondaryDark};
    padding: 0 8px;
  }
`;

const Row = styled.tr`
  height: 44px;
  cursor: pointer;

  &:hover {
    background: #2a2a2a;
  }

  td {
    border-bottom: 2px solid ${theme.colors.secondaryDark};
    font-size: 12px;
    padding: 0 8px;
  }
`;

const SessionCell = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${theme.colors.grey};
`;

const Time = styled.span`
  font-family: "Roboto Condensed", sans-serif;
  font-size: 17px;
  font-weight: 700;
  color: ${theme.colors.green};
`;

const DateCell = styled.span`
  font-size: 11px;
  color: ${theme.colors.grey};
  white-space: nowrap;
`;

const HolderCell = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CarCell = styled.span`
  font-size: 11px;
  color: ${theme.colors.grey};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TyreIcon = styled.img`
  width: 18px;
  height: 18px;
`;

export function TrackModal({ track, initialConfiguration, onClose, onSelectDriver }) {
  const [configuration, setConfiguration] = useState(initialConfiguration || null);
  const [car, setCar] = useState("all");

  useEffect(() => {
    setConfiguration(initialConfiguration || null);
    setCar("all");
  }, [track, initialConfiguration]);

  const configurations = useMemo(() => {
    const names = new Set();
    (track?.configurations || []).forEach((c) => names.add(c.configuration));
    return Array.from(names);
  }, [track]);

  const carOptions = useMemo(() => {
    const cars = new Set();
    (track?.configurations || [])
      .filter((c) => c.configuration === configuration)
      .forEach((c) => cars.add(c.car));
    return Array.from(cars);
  }, [track, configuration]);

  const rows = useMemo(() => {
    return (track?.configurations || [])
      .filter((c) => c.configuration === configuration && (car === "all" || c.car === car))
      .slice()
      .sort((a, b) => a.time - b.time);
  }, [track, configuration, car]);

  const configImage = useMemo(() => {
    const entry = (track?.configurations || []).find((c) => c.configuration === configuration && c.image);
    return entry?.image || track?.image || null;
  }, [track, configuration]);

  if (!track) return null;

  return (
    <Dialog
      open={Boolean(track)}
      onClose={onClose}
      maxWidth={false}
      sx={{ ...modalOverlaySx, "& .MuiDialog-paper": { width: "100%", maxWidth: 800 } }}
      PaperProps={{ sx: modalPaperSx }}
    >
      <ModalHead>
        {getFlag(track.flag) && <FlagChip src={getFlag(track.flag)} alt={track.country} />}
        <ModalHeadText>
          <ModalTitle>{track.name}</ModalTitle>
          <ModalNote>У кожної конфігурації, машини та симулятора — свій рекорд кола</ModalNote>
        </ModalHeadText>
        <CloseButton onClick={onClose} aria-label="Закрити">
          ✕
        </CloseButton>
      </ModalHead>

      {configImage && (
        <ImageArea>
          <TrackImage src={configImage} alt={`${track.name} — ${configuration}`} />
        </ImageArea>
      )}

      <Controls>
        <Dropdown
          label="Конфігурація"
          value={configuration || ""}
          options={configurations.map((c) => ({ value: c, label: c }))}
          onChange={(v) => {
            setConfiguration(v);
            setCar("all");
          }}
        />
        <Dropdown
          label="Машина"
          value={car}
          options={[{ value: "all", label: "Усі машини" }, ...carOptions.map((c) => ({ value: c, label: c }))]}
          onChange={setCar}
        />
      </Controls>

      <Body>
        {rows.length === 0 ? (
          <NoDataMessage>Для цієї комбінації конфігурації та машини рекордів немає</NoDataMessage>
        ) : (
          <Table>
            <thead>
              <HeadRow>
                <th style={{ width: 100 }}>Сесія</th>
                <th style={{ width: 130 }}>Час</th>
                <th style={{ width: 100 }}>Дата</th>
                <th>Рекордсмен</th>
                <th style={{ width: 120 }}>Машина</th>
                <th style={{ width: 70, textAlign: "right" }}>Гума</th>
              </HeadRow>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <Row
                  key={idx}
                  onClick={() => onSelectDriver(r.holderId)}
                >
                  <td>
                    <SessionCell>{SESSION_LABELS[r.sessionType] || r.sessionType}</SessionCell>
                  </td>
                  <td>
                    <Time>{msToTime(r.time)}</Time>
                  </td>
                  <td>
                    <DateCell>{formatDate(r.raceDate) || "—"}</DateCell>
                  </td>
                  <td>
                    <HolderCell>{r.holderName}</HolderCell>
                  </td>
                  <td>
                    <CarCell>{r.car}</CarCell>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {tyreImages[r.tyreCompound] && (
                      <TyreIcon src={tyreImages[r.tyreCompound]} alt={r.tyreCompound} />
                    )}
                  </td>
                </Row>
              ))}
            </tbody>
          </Table>
        )}
      </Body>
    </Dialog>
  );
}
